import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Users, TrendingUp, FileText, DollarSign, Activity, 
  Search, ChevronRight, AlertTriangle, CheckCircle,
  Clock, ArrowUpRight, ArrowDownRight, RefreshCw,
  BarChart3, PieChart, Settings, LogOut, Zap, AlertCircle,
  Mail, Phone, MapPin, Calendar, UserCheck, Repeat, ChevronLeft, Trash2
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendUp }) => (
  <Card className="bg-card/50 border-white/10">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg ${trendUp ? 'bg-green-500/10' : trend ? 'bg-red-500/10' : 'bg-primary/10'}`}>
          <Icon className={`w-5 h-5 ${trendUp ? 'text-green-500' : trend ? 'text-red-500' : 'text-primary'}`} />
        </div>
      </div>
      {trend && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trendUp ? 'text-green-500' : 'text-red-500'}`}>
          {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          <span>{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// User Row Component
const UserRow = ({ user, onClick }) => (
  <div 
    className="flex items-center justify-between p-4 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0"
    onClick={onClick}
    data-testid={`user-row-${user.user_id}`}
  >
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
        <span className="text-primary font-medium">
          {user.name?.charAt(0) || user.email?.charAt(0) || "?"}
        </span>
      </div>
      <div>
        <p className="font-medium">{user.name || "No name"}</p>
        <p className="text-sm text-muted-foreground">{user.email}</p>
        {user.company_name && (
          <p className="text-xs text-muted-foreground">{user.company_name}</p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-4">
      <div className="text-right">
        <p className="text-sm">{user.quotes_count || 0} quotes</p>
        <p className="text-xs text-muted-foreground">
          {user.accepted_quotes || 0} accepted
        </p>
      </div>
      <Badge variant={
        user.account_status === "active" ? "default" :
        user.account_status === "trial" ? "secondary" :
        user.account_status === "churned" ? "destructive" : "outline"
      }>
        {user.account_status || "trial"}
      </Badge>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
  </div>
);

const AdminDashboard = ({ user, API: apiUrl }) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [quoteAnalytics, setQuoteAnalytics] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [creditBalance, setCreditBalance] = useState("");
  const [creditNotes, setCreditNotes] = useState("");
  const [updatingCredits, setUpdatingCredits] = useState(false);
  
  // Customer state
  const [customers, setCustomers] = useState([]);
  const [customerStats, setCustomerStats] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerSort, setCustomerSort] = useState("last_activity");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Check admin access
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await fetch(`${API}/admin/check`, {
          credentials: "include"
        });
        if (response.ok) {
          setIsAdmin(true);
          fetchDashboardData();
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch all data in parallel
      const [overviewRes, usersRes, quotesRes, revenueRes, healthRes, creditsRes] = await Promise.all([
        fetch(`${API}/admin/overview`, { credentials: "include" }),
        fetch(`${API}/admin/users?limit=10`, { credentials: "include" }),
        fetch(`${API}/admin/analytics/quotes?days=30`, { credentials: "include" }),
        fetch(`${API}/admin/analytics/revenue`, { credentials: "include" }),
        fetch(`${API}/admin/system/health`, { credentials: "include" }),
        fetch(`${API}/admin/credits/status`, { credentials: "include" })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users);
      }
      if (quotesRes.ok) setQuoteAnalytics(await quotesRes.json());
      if (revenueRes.ok) setRevenueData(await revenueRes.json());
      if (healthRes.ok) setSystemHealth(await healthRes.json());
      if (creditsRes.ok) {
        const data = await creditsRes.json();
        setCreditStatus(data);
        setCreditBalance(data.balance?.toString() || "");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    }
  };

  const updateCreditBalance = async () => {
    const balance = parseFloat(creditBalance);
    if (isNaN(balance) || balance < 0) {
      toast.error("Please enter a valid balance");
      return;
    }

    setUpdatingCredits(true);
    try {
      const response = await fetch(`${API}/admin/credits/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ balance, notes: creditNotes || null })
      });

      if (response.ok) {
        const data = await response.json();
        setCreditStatus({
          ...creditStatus,
          balance: data.balance,
          is_low: data.is_low,
          maintenance_mode: data.maintenance_mode
        });
        setCreditNotes("");
        
        if (data.alert_sent) {
          toast.success("Balance updated and alert email sent!");
        } else {
          toast.success("Balance updated successfully");
        }
      } else {
        toast.error("Failed to update balance");
      }
    } catch (error) {
      toast.error("Failed to update balance");
    } finally {
      setUpdatingCredits(false);
    }
  };

  // Plumber (Trade Office Customer) functions
  const fetchCustomers = async (search = "", sort = "last_activity") => {
    setLoadingCustomers(true);
    try {
      const params = new URLSearchParams({ sort_by: sort, limit: "100" });
      if (search) params.append("search", search);
      
      const response = await fetch(`${API}/admin/plumbers?${params}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.plumbers);
      }
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchCustomerStats = async () => {
    try {
      const response = await fetch(`${API}/admin/plumbers/stats`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerStats(data);
      }
    } catch (error) {
      console.error("Failed to load customer stats");
    }
  };

  const fetchCustomerDetail = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/plumbers/${encodeURIComponent(userId)}/activity`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerDetail(data);
        setSelectedCustomer(userId);
      }
    } catch (error) {
      toast.error("Failed to load customer details");
    }
  };

  const [selectedQuote, setSelectedQuote] = useState(null);
  
  const fetchQuoteDetail = async (quoteId) => {
    try {
      const response = await fetch(`${API}/admin/quotes/${quoteId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedQuote(data);
      }
    } catch (error) {
      toast.error("Failed to load quote details");
    }
  };

  const searchUsers = async () => {
    try {
      const response = await fetch(
        `${API}/admin/users?search=${encodeURIComponent(searchQuery)}&limit=50`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      toast.error("Search failed");
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUserDetail(data);
        setSelectedUser(userId);
      }
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success("Status updated");
        fetchUserDetail(userId);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const extendTrial = async (userId, days) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/extend-trial`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ days })
      });
      if (response.ok) {
        toast.success(`Trial extended by ${days} days`);
        fetchUserDetail(userId);
      }
    } catch (error) {
      toast.error("Failed to extend trial");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to access the admin dashboard.
            </p>
            <Button onClick={() => navigate("/dashboard")}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const COLORS = ['#f97316', '#3b82f6', '#22c55e', '#eab308', '#8b5cf6'];

  return (
    <div className="min-h-screen bg-background">
      {/* Maintenance Mode Banner */}
      {creditStatus?.maintenance_mode && (
        <div className="bg-gradient-to-r from-red-600 to-red-500 text-white px-4 py-3" data-testid="maintenance-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 animate-pulse" />
              <div>
                <span className="font-semibold">Maintenance Mode Active</span>
                <span className="mx-2">•</span>
                <span className="text-red-100">Emergent credits low (£{creditStatus.balance?.toFixed(2)}). Quote generation may be affected.</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="bg-white/10 border-white/30 hover:bg-white/20 text-white"
              onClick={() => setActiveTab("health")}
            >
              View Details
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-white/10 bg-card/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-primary">Trade Office Admin</h1>
            <Badge variant="outline" className="text-xs">Internal</Badge>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchDashboardData}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/dashboard")}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Exit Admin
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="border-b border-white/10 bg-card/30">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1">
            {[
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "users", label: "Users", icon: Users },
              { id: "customers", label: "Customers", icon: UserCheck },
              { id: "quotes", label: "Quotes", icon: FileText },
              { id: "revenue", label: "Revenue", icon: DollarSign },
              { id: "health", label: "System", icon: Activity }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { 
                  setActiveTab(tab.id); 
                  setSelectedUser(null); 
                  setSelectedCustomer(null);
                  if (tab.id === "customers" && customers.length === 0) {
                    fetchCustomers();
                    fetchCustomerStats();
                  }
                }}
                className={`px-4 py-3 flex items-center gap-2 text-sm border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? "border-primary text-primary" 
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === "overview" && overview && (
          <div className="space-y-6">
            {/* KPI Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard
                title="Total Users"
                value={overview.user_metrics.total_users}
                subtitle={`${overview.user_metrics.new_30d} new (30d)`}
                icon={Users}
              />
              <KPICard
                title="Active Users (MAU)"
                value={overview.user_metrics.mau}
                subtitle={`${overview.user_metrics.dau} today`}
                icon={Activity}
                trendUp={overview.user_metrics.mau > 0}
              />
              <KPICard
                title="Quotes (30d)"
                value={overview.product_usage.quotes_30d}
                subtitle={`${overview.product_usage.quotes_today} today`}
                icon={FileText}
              />
              <KPICard
                title="Acceptance Rate"
                value={`${overview.product_usage.acceptance_rate}%`}
                subtitle={`Avg value: £${overview.product_usage.avg_quote_value}`}
                icon={CheckCircle}
                trendUp={overview.product_usage.acceptance_rate > 30}
              />
            </div>

            {/* Revenue Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard
                title="MRR"
                value={`£${overview.revenue.mrr.toLocaleString()}`}
                icon={DollarSign}
                trendUp={overview.revenue.mrr > 0}
              />
              <KPICard
                title="ARR"
                value={`£${overview.revenue.arr.toLocaleString()}`}
                icon={TrendingUp}
              />
              <KPICard
                title="Paying Customers"
                value={overview.revenue.paying_customers}
                icon={Users}
              />
              <KPICard
                title="Trial Users"
                value={overview.user_metrics.trial_users}
                icon={Clock}
              />
            </div>

            {/* Charts Row */}
            {quoteAnalytics && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-card/50 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-base">Quotes Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={quoteAnalytics.quotes_by_day}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="_id" stroke="#888" fontSize={12} />
                          <YAxis stroke="#888" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1a1a2e', 
                              border: '1px solid #333',
                              borderRadius: '8px'
                            }} 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#f97316" 
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card/50 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-base">Quote Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={Object.entries(quoteAnalytics.quotes_by_status).map(([name, value]) => ({ name, value }))}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {Object.keys(quoteAnalytics.quotes_by_status).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Top Users */}
            {quoteAnalytics?.top_users && (
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">Top Users by Quotes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {quoteAnalytics.top_users.slice(0, 5).map((u, i) => (
                      <div key={u._id} className="flex items-center justify-between p-2 rounded hover:bg-white/5">
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground w-6">#{i + 1}</span>
                          <div>
                            <p className="font-medium">{u.name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground">{u.company_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{u.quote_count} quotes</p>
                          <p className="text-xs text-muted-foreground">£{u.total_value?.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && !selectedUser && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by name, email, or company..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchUsers()}
                  data-testid="user-search"
                />
              </div>
              <Button onClick={searchUsers}>Search</Button>
            </div>

            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-base">Users</CardTitle>
                <CardDescription>Click on a user to view details</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {users.map(u => (
                  <UserRow key={u.user_id} user={u} onClick={() => fetchUserDetail(u.user_id)} />
                ))}
                {users.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No users found</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* User Detail View */}
        {activeTab === "users" && selectedUser && userDetail && (
          <div className="space-y-6">
            <Button variant="ghost" onClick={() => setSelectedUser(null)}>
              ← Back to Users
            </Button>

            <div className="grid md:grid-cols-3 gap-6">
              {/* User Info */}
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">User Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{userDetail.user.name || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{userDetail.user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Company</p>
                    <p className="font-medium">{userDetail.user.company_name || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge>{userDetail.user.account_status || "trial"}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Plan</p>
                    <p className="font-medium">{userDetail.user.subscription_plan || "Free"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Signed Up</p>
                    <p className="font-medium">
                      {userDetail.user.created_at 
                        ? new Date(userDetail.user.created_at).toLocaleDateString() 
                        : "Unknown"}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">Statistics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-2xl font-bold">{userDetail.stats.total_quotes}</p>
                      <p className="text-xs text-muted-foreground">Total Quotes</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-2xl font-bold">{userDetail.stats.quotes_30d}</p>
                      <p className="text-xs text-muted-foreground">Last 30 Days</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-2xl font-bold">{userDetail.stats.accepted_quotes}</p>
                      <p className="text-xs text-muted-foreground">Accepted</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-2xl font-bold">{userDetail.stats.acceptance_rate}%</p>
                      <p className="text-xs text-muted-foreground">Accept Rate</p>
                    </div>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-2xl font-bold">£{userDetail.stats.avg_quote_value}</p>
                    <p className="text-xs text-muted-foreground">Avg Quote Value</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-2xl font-bold">£{userDetail.stats.total_quote_value?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Quote Value</p>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">Admin Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Change Subscription Plan */}
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground font-medium">Subscription Plan</p>
                    <div className="flex flex-wrap gap-2">
                      {["trial", "starter", "professional", "business", "pilot"].map(plan => (
                        <Button
                          key={plan}
                          variant={userDetail.user.subscription_plan === plan ? "default" : "outline"}
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await fetch(`${API}/admin/users/${selectedUser}/plan`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json" },
                                credentials: "include",
                                body: JSON.stringify({ plan, reset_usage: false })
                              });
                              if (response.ok) {
                                const data = await response.json();
                                toast.success(data.message);
                                fetchUserDetail(selectedUser);
                              } else {
                                const error = await response.json();
                                toast.error(error.detail || "Failed to change plan");
                              }
                            } catch (e) {
                              toast.error("Failed to change plan");
                            }
                          }}
                          data-testid={`plan-btn-${plan}`}
                        >
                          {plan}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Current: {userDetail.user.subscription_plan || "trial"} • 
                      Quota: {userDetail.user.subscription_plan === "pilot" ? "Unlimited" : 
                        userDetail.user.subscription_plan === "business" ? "150/mo" :
                        userDetail.user.subscription_plan === "professional" ? "75/mo" :
                        userDetail.user.subscription_plan === "starter" ? "25/mo" : "3 trial"}
                    </p>
                  </div>

                  {/* Assign Credits */}
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-sm text-muted-foreground font-medium">Add PAYG Credits</p>
                    <div className="flex gap-2">
                      {[5, 10, 25, 50].map(credits => (
                        <Button
                          key={credits}
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await fetch(`${API}/admin/users/${selectedUser}/credits`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                credentials: "include",
                                body: JSON.stringify({ credits, reason: "Admin assignment" })
                              });
                              if (response.ok) {
                                const data = await response.json();
                                toast.success(`Added ${credits} credits. New balance: ${data.new_balance}`);
                                fetchUserDetail(selectedUser);
                              } else {
                                const error = await response.json();
                                toast.error(error.detail || "Failed to add credits");
                              }
                            } catch (e) {
                              toast.error("Failed to add credits");
                            }
                          }}
                          data-testid={`credits-btn-${credits}`}
                        >
                          +{credits}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Current PAYG credits: {userDetail.user.payg_credits || 0}
                    </p>
                  </div>

                  {/* Usage Stats */}
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-sm text-muted-foreground font-medium">Quota Usage</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 bg-white/5 rounded">
                        <p className="text-muted-foreground text-xs">Used this month</p>
                        <p className="font-medium">{userDetail.user.quotes_used_this_month || 0}</p>
                      </div>
                      <div className="p-2 bg-white/5 rounded">
                        <p className="text-muted-foreground text-xs">Borrowed</p>
                        <p className="font-medium">{userDetail.user.quotes_borrowed_from_next || 0}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/admin/users/${selectedUser}/plan`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({ 
                              plan: userDetail.user.subscription_plan || "trial", 
                              reset_usage: true 
                            })
                          });
                          if (response.ok) {
                            toast.success("Usage counter reset");
                            fetchUserDetail(selectedUser);
                          }
                        } catch (e) {
                          toast.error("Failed to reset usage");
                        }
                      }}
                    >
                      Reset Usage Counter
                    </Button>
                  </div>
                  
                  {/* Change Status */}
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-sm text-muted-foreground font-medium">Account Status</p>
                    <div className="flex flex-wrap gap-2">
                      {["trial", "active", "pilot", "churned"].map(status => (
                        <Button
                          key={status}
                          variant={userDetail.user.account_status === status ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateUserStatus(selectedUser, status)}
                        >
                          {status}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Extend Trial */}
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-sm text-muted-foreground font-medium">Extend Trial</p>
                    <div className="flex gap-2">
                      {[7, 14, 30].map(days => (
                        <Button
                          key={days}
                          variant="outline"
                          size="sm"
                          onClick={() => extendTrial(selectedUser, days)}
                        >
                          +{days} days
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Reset Password */}
                  {userDetail.user.auth_provider !== "google" && (
                    <div className="space-y-2 pt-2 border-t border-white/10">
                      <p className="text-sm text-muted-foreground font-medium">Reset Password</p>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          placeholder="New password (min 8 chars)"
                          className="flex-1 px-3 py-1 text-sm bg-white/5 border border-white/10 rounded-md"
                          id={`new-password-${selectedUser}`}
                          data-testid="reset-password-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="reset-password-btn"
                          onClick={async () => {
                            const input = document.getElementById(`new-password-${selectedUser}`);
                            const newPassword = input?.value;
                            if (!newPassword || newPassword.length < 8) {
                              toast.error("Password must be at least 8 characters");
                              return;
                            }
                            try {
                              const response = await fetch(`${API}/admin/users/${selectedUser}/reset-password`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                credentials: "include",
                                body: JSON.stringify({ new_password: newPassword })
                              });
                              if (response.ok) {
                                const data = await response.json();
                                toast.success(data.message || "Password reset successfully");
                                input.value = "";
                              } else {
                                const error = await response.json();
                                toast.error(error.detail || "Failed to reset password");
                              }
                            } catch (e) {
                              toast.error("Failed to reset password");
                            }
                          }}
                        >
                          Reset
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        This will invalidate all user sessions
                      </p>
                    </div>
                  )}
                  {userDetail.user.auth_provider === "google" && (
                    <div className="space-y-2 pt-2 border-t border-white/10">
                      <p className="text-sm text-muted-foreground font-medium">Reset Password</p>
                      <p className="text-xs text-muted-foreground">
                        This user signed up with Google - password cannot be reset
                      </p>
                    </div>
                  )}

                  {/* Danger Zone */}
                  <div className="pt-4 border-t border-white/10">
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="w-full"
                      onClick={async () => {
                        if (confirm("Are you sure you want to disable this account?")) {
                          try {
                            await fetch(`${API}/admin/users/${selectedUser}/disable`, {
                              method: "POST",
                              credentials: "include"
                            });
                            toast.success("Account disabled");
                            fetchUserDetail(selectedUser);
                          } catch (e) {
                            toast.error("Failed to disable account");
                          }
                        }
                      }}
                    >
                      Disable Account
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Quotes */}
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-base">Recent Quotes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {userDetail.recent_quotes.map(q => (
                    <div key={q.quote_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="font-medium">{q.customer_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(q.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">£{q.total?.toLocaleString()}</span>
                        <Badge variant={
                          q.status === "accepted" ? "default" :
                          q.status === "rejected" ? "destructive" : "secondary"
                        }>
                          {q.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {userDetail.recent_quotes.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No quotes yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === "quotes" && quoteAnalytics && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">Daily Quote Volume</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={quoteAnalytics.quotes_by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="_id" stroke="#888" fontSize={10} angle={-45} textAnchor="end" height={80} />
                        <YAxis stroke="#888" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1a1a2e', 
                            border: '1px solid #333',
                            borderRadius: '8px'
                          }} 
                        />
                        <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">Accepted vs Total</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={quoteAnalytics.quotes_by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="_id" stroke="#888" fontSize={10} angle={-45} textAnchor="end" height={80} />
                        <YAxis stroke="#888" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1a1a2e', 
                            border: '1px solid #333',
                            borderRadius: '8px'
                          }} 
                        />
                        <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Total" />
                        <Bar dataKey="accepted" fill="#22c55e" radius={[4, 4, 0, 0]} name="Accepted" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-base">Quote Value Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={quoteAnalytics.avg_value_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="_id" stroke="#888" fontSize={10} />
                      <YAxis stroke="#888" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a2e', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`£${value?.toFixed(2)}`, 'Avg Value']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="avg_value" 
                        stroke="#22c55e" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === "revenue" && revenueData && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              <KPICard
                title="Total MRR"
                value={`£${revenueData.mrr.total.toLocaleString()}`}
                icon={DollarSign}
                trendUp
              />
              <KPICard
                title="ARR"
                value={`£${revenueData.arr.toLocaleString()}`}
                icon={TrendingUp}
              />
              <KPICard
                title="PAYG Revenue (30d)"
                value={`£${revenueData.payg_revenue_30d.toLocaleString()}`}
                icon={FileText}
              />
              <KPICard
                title="Trials Ending (7d)"
                value={revenueData.trials_ending_7d}
                icon={Clock}
                trendUp={false}
                trend={revenueData.trials_ending_7d > 0 ? "Action needed" : undefined}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">MRR by Plan</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(revenueData.mrr.by_plan).map(([plan, data]) => (
                      <div key={plan} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="font-medium capitalize">{plan}</p>
                          <p className="text-xs text-muted-foreground">{data.count} customers</p>
                        </div>
                        <p className="text-lg font-bold">£{data.mrr.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base">Recent Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {revenueData.recent_transactions.map((t, i) => (
                      <div key={i} className="flex items-center justify-between p-2 border-b border-white/5">
                        <div>
                          <p className="text-sm font-medium capitalize">{t.plan}</p>
                          <p className="text-xs text-muted-foreground">
                            {t.created_at ? new Date(t.created_at).toLocaleDateString() : "Unknown"}
                          </p>
                        </div>
                        <Badge variant={t.status === "completed" ? "default" : "secondary"}>
                          {t.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Customers Tab - Trade Office's Plumber Customers */}
        {activeTab === "customers" && (
          <div className="space-y-6">
            {/* Customer Stats Cards */}
            {customerStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard
                  title="Total Plumbers"
                  value={customerStats.total_plumbers}
                  subtitle={`${customerStats.new_this_month} new this month`}
                  icon={UserCheck}
                />
                <KPICard
                  title="Active This Week"
                  value={customerStats.active_this_week}
                  subtitle={`${customerStats.paying_customers} paying`}
                  icon={Activity}
                  trendUp={customerStats.active_this_week > 5}
                />
                <KPICard
                  title="Trial → Paid"
                  value={`${customerStats.conversion_rate}%`}
                  subtitle={`${customerStats.trial_users} trials`}
                  icon={TrendingUp}
                  trendUp={customerStats.conversion_rate > 10}
                />
                <KPICard
                  title="Total Quote Value"
                  value={`£${(customerStats.total_quote_value || 0).toLocaleString()}`}
                  subtitle={`${customerStats.avg_quotes_per_plumber} avg quotes/plumber`}
                  icon={DollarSign}
                />
              </div>
            )}

            {/* Quote Detail Modal */}
            {selectedQuote && (
              <Card className="bg-card/50 border-primary/50 border-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Quote #{selectedQuote.quote.quote_id?.slice(-8).toUpperCase()}
                      </CardTitle>
                      <CardDescription>
                        Created {new Date(selectedQuote.quote.created_at).toLocaleString()}
                      </CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedQuote(null)}>
                      Close
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-2">Customer Details</h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-muted-foreground">Name:</span> {selectedQuote.quote.customer_name || "N/A"}</p>
                        <p><span className="text-muted-foreground">Email:</span> {selectedQuote.quote.customer_email || "N/A"}</p>
                        <p><span className="text-muted-foreground">Phone:</span> {selectedQuote.quote.customer_phone || "N/A"}</p>
                        <p><span className="text-muted-foreground">Address:</span> {selectedQuote.quote.customer_address || "N/A"}</p>
                      </div>
                      <h4 className="font-semibold mb-2 mt-4">Job Description</h4>
                      <p className="text-sm text-muted-foreground">{selectedQuote.quote.job_description}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Quote Summary</h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-muted-foreground">Status:</span> <Badge variant={selectedQuote.quote.status === "accepted" ? "default" : "secondary"}>{selectedQuote.quote.status}</Badge></p>
                        <p><span className="text-muted-foreground">Materials:</span> £{selectedQuote.quote.materials_total?.toFixed(2)}</p>
                        <p><span className="text-muted-foreground">Labor:</span> {selectedQuote.quote.labor_hours}hrs @ £{selectedQuote.quote.hourly_rate}/hr = £{selectedQuote.quote.labor_total?.toFixed(2)}</p>
                        <p><span className="text-muted-foreground">Margin:</span> {selectedQuote.quote.margin_percentage}% = £{selectedQuote.quote.margin_amount?.toFixed(2)}</p>
                        <p className="text-lg font-bold mt-2">Total: £{selectedQuote.quote.total?.toFixed(2)}</p>
                      </div>
                      <h4 className="font-semibold mb-2 mt-4">Items ({selectedQuote.quote.items?.length || 0})</h4>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {selectedQuote.quote.items?.map((item, idx) => (
                          <div key={idx} className="text-xs flex justify-between">
                            <span className="truncate flex-1">{item.description}</span>
                            <span className="ml-2">£{(item.quantity * item.unit_price).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Plumber Detail View */}
            {selectedCustomer && customerDetail && !selectedQuote ? (
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); }}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back
                      </Button>
                      <div>
                        <CardTitle className="text-lg">{customerDetail.plumber.name || customerDetail.plumber.company_name || "Unknown"}</CardTitle>
                        <CardDescription className="flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {customerDetail.plumber.email}
                          </span>
                          <Badge variant={customerDetail.plumber.subscription_plan === "trial" ? "secondary" : "default"}>
                            {customerDetail.plumber.subscription_plan || "trial"}
                          </Badge>
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Plumber Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-background/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Total Quotes</p>
                      <p className="text-2xl font-bold">{customerDetail.stats.total_quotes}</p>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Total Value</p>
                      <p className="text-2xl font-bold">£{customerDetail.stats.total_value?.toLocaleString()}</p>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Accepted</p>
                      <p className="text-2xl font-bold text-green-500">{customerDetail.stats.accepted_quotes}</p>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Accept Rate</p>
                      <p className="text-2xl font-bold">{customerDetail.stats.acceptance_rate}%</p>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Unique Customers</p>
                      <p className="text-2xl font-bold">{customerDetail.stats.unique_customers}</p>
                    </div>
                  </div>

                  {/* Activity Timeline with clickable quotes */}
                  <h3 className="font-semibold mb-4">Activity Timeline</h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {customerDetail.activity.map((event, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-start gap-4 p-3 bg-background/30 rounded-lg ${event.quote_id ? 'cursor-pointer hover:bg-background/50' : ''}`}
                        onClick={() => event.quote_id && fetchQuoteDetail(event.quote_id)}
                      >
                        <div className={`p-2 rounded-full ${
                          event.type === "quote_accepted" ? "bg-green-500/20" :
                          event.type === "quote_sent" ? "bg-blue-500/20" : "bg-primary/20"
                        }`}>
                          {event.type === "quote_accepted" ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : event.type === "quote_sent" ? (
                            <Mail className="w-4 h-4 text-blue-500" />
                          ) : (
                            <FileText className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {event.type === "quote_created" && `Quote created for ${event.customer_name || 'Unknown'} - £${event.total?.toFixed(2)}`}
                            {event.type === "quote_sent" && `Quote sent to ${event.sent_to || event.customer_name}`}
                            {event.type === "quote_accepted" && `Quote accepted by ${event.customer_name} - £${event.total?.toFixed(2)}`}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {event.job_description && event.job_description.substring(0, 80)}...
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            <span>{new Date(event.date).toLocaleString()}</span>
                            {event.quote_id && <span className="text-primary">Click to view quote</span>}
                          </div>
                        </div>
                        <Badge variant={event.status === "accepted" ? "default" : event.status === "sent" ? "secondary" : "outline"}>
                          {event.status || event.type.replace("quote_", "")}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : !selectedQuote && (
              /* Plumber Customer List */
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Plumber Customers</CardTitle>
                      <CardDescription>Trade Office customers (plumbers) and their activity</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by email, name, company..."
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && fetchCustomers(customerSearch, customerSort)}
                          className="pl-10 w-64"
                          data-testid="customer-search"
                        />
                      </div>
                      <select
                        value={customerSort}
                        onChange={(e) => {
                          setCustomerSort(e.target.value);
                          fetchCustomers(customerSearch, e.target.value);
                        }}
                        className="bg-background border border-white/10 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="last_activity">Last Activity</option>
                        <option value="total_value">Total Value</option>
                        <option value="total_quotes">Most Quotes</option>
                        <option value="created_at">Sign Up Date</option>
                        <option value="name">Name</option>
                      </select>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => fetchCustomers(customerSearch, customerSort)}
                      >
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingCustomers ? (
                    <div className="flex justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : customers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No customers found
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {customers.map((customer) => (
                        <div
                          key={customer.user_id}
                          className="flex items-center justify-between p-4 hover:bg-white/5 cursor-pointer transition-colors"
                          onClick={() => fetchCustomerDetail(customer.user_id)}
                          data-testid={`customer-row-${customer.user_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-primary font-medium">
                                {(customer.name || customer.company_name || customer.email)?.charAt(0)?.toUpperCase() || "?"}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{customer.name || customer.company_name || "Unknown"}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-2">
                                <Mail className="w-3 h-3" />
                                {customer.email}
                              </p>
                              {customer.company_name && customer.name && (
                                <p className="text-xs text-muted-foreground">{customer.company_name}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <Badge variant={
                                customer.subscription_plan === "professional" ? "default" :
                                customer.subscription_plan === "starter" ? "secondary" : "outline"
                              }>
                                {customer.subscription_plan || "trial"}
                              </Badge>
                              <p className="text-xs text-muted-foreground mt-1">
                                Joined {new Date(customer.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">£{(customer.total_value || 0).toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">
                                {customer.total_quotes || 0} quotes • {customer.accepted_quotes || 0} accepted
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium">{customer.acceptance_rate || 0}% accept</p>
                              <p className="text-xs text-muted-foreground">
                                Last: {customer.last_activity ? new Date(customer.last_activity).toLocaleDateString() : "Never"}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* System Health Tab */}
        {activeTab === "health" && systemHealth && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Quote Generation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">24h Total</span>
                      <span className="font-medium">{systemHealth.quote_generation.total_24h}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Low Confidence</span>
                      <span className="font-medium text-yellow-500">{systemHealth.quote_generation.low_confidence}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Success Rate</span>
                      <span className={`font-medium ${systemHealth.quote_generation.success_rate > 90 ? 'text-green-500' : 'text-yellow-500'}`}>
                        {systemHealth.quote_generation.success_rate}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Email Delivery
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sent (24h)</span>
                      <span className="font-medium">{systemHealth.email_delivery.sent_24h}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Payments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">24h Total</span>
                      <span className="font-medium">{systemHealth.payments.total_24h}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Failed</span>
                      <span className={`font-medium ${systemHealth.payments.failed_24h > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {systemHealth.payments.failed_24h}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Success Rate</span>
                      <span className={`font-medium ${systemHealth.payments.success_rate > 95 ? 'text-green-500' : 'text-yellow-500'}`}>
                        {systemHealth.payments.success_rate}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card/50 border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-muted-foreground">Last checked: {new Date(systemHealth.checked_at).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Emergent Credits Section */}
            <Card className={`border-2 ${creditStatus?.is_low ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-card/50'}`}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className={`w-4 h-4 ${creditStatus?.is_low ? 'text-red-500' : 'text-primary'}`} />
                  Emergent LLM Credits
                  {creditStatus?.is_low && (
                    <Badge variant="destructive" className="ml-2">LOW</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Monitor and update your Emergent credit balance. Alert sent when below £20.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Current Status */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Current Balance</span>
                      <span className={`text-2xl font-bold ${creditStatus?.is_low ? 'text-red-500' : 'text-green-500'}`}>
                        £{creditStatus?.balance?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Alert Threshold</span>
                      <span className="font-medium">£{creditStatus?.threshold || 20}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Maintenance Mode</span>
                      <Badge variant={creditStatus?.maintenance_mode ? "destructive" : "secondary"}>
                        {creditStatus?.maintenance_mode ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    {creditStatus?.last_updated && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Updated</span>
                        <span className="text-sm">{new Date(creditStatus.last_updated).toLocaleString()}</span>
                      </div>
                    )}
                    {creditStatus?.last_alert_sent && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Alert Sent</span>
                        <span className="text-sm text-yellow-500">{new Date(creditStatus.last_alert_sent).toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Update Form */}
                  <div className="space-y-4 border-l border-white/10 pl-6">
                    <p className="text-sm text-muted-foreground">
                      Check your balance in the Emergent dashboard and update here:
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">New Balance (£)</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={creditBalance}
                          onChange={(e) => setCreditBalance(e.target.value)}
                          placeholder="Enter current balance"
                          className="bg-background"
                          data-testid="credit-balance-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Notes (optional)</label>
                        <Input
                          value={creditNotes}
                          onChange={(e) => setCreditNotes(e.target.value)}
                          placeholder="e.g., Topped up £50"
                          className="bg-background"
                        />
                      </div>
                      <Button 
                        onClick={updateCreditBalance}
                        disabled={updatingCredits}
                        className="w-full"
                        data-testid="update-credits-btn"
                      >
                        {updatingCredits ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-2" />
                            Update Balance
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      If balance drops below £20, an alert email will be sent to tobygeorgesmith@outlook.com
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Database Cleanup - Super Admin Only */}
            {user?.email?.toLowerCase() === "tobygeorgesmith@outlook.com" && (
              <Card className="border-2 border-red-500/30 bg-red-500/5">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-4 h-4" />
                    Database Cleanup (Super Admin)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Remove all test users and their data. Only keeps specified admin accounts.
                  </p>
                  <div className="space-y-3">
                    <div className="p-3 bg-white/5 rounded text-sm">
                      <p className="font-medium mb-2">Users to keep:</p>
                      <ul className="list-disc list-inside text-muted-foreground">
                        <li>toby@tradesoffice.co.uk</li>
                        <li>doug@tradesoffice.co.uk</li>
                        <li>tobygeorgesmith@outlook.com</li>
                      </ul>
                    </div>
                    <Button 
                      variant="destructive"
                      className="w-full"
                      data-testid="cleanup-database-btn"
                      onClick={async () => {
                        // First preview
                        const previewRes = await fetch(`${API}/admin/database/cleanup`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            keep_emails: ["toby@tradesoffice.co.uk", "doug@tradesoffice.co.uk", "tobygeorgesmith@outlook.com"],
                            confirm: false
                          })
                        });
                        const preview = await previewRes.json();
                        
                        if (!confirm(`This will delete ${preview.users_to_delete} users and all their data.\n\nUsers to keep: ${preview.users_to_keep?.join(", ")}\n\nAre you sure?`)) {
                          return;
                        }
                        
                        // Execute cleanup
                        try {
                          const res = await fetch(`${API}/admin/database/cleanup`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({
                              keep_emails: ["toby@tradesoffice.co.uk", "doug@tradesoffice.co.uk", "tobygeorgesmith@outlook.com"],
                              confirm: true
                            })
                          });
                          const result = await res.json();
                          if (result.success) {
                            toast.success(`Cleanup complete! Deleted: ${result.results.deleted.users} users, ${result.results.deleted.quotes} quotes, ${result.results.deleted.payment_transactions} transactions`);
                            fetchData(); // Refresh dashboard
                          } else {
                            toast.error(result.detail || "Cleanup failed");
                          }
                        } catch (e) {
                          toast.error("Cleanup failed: " + e.message);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clean Database (Remove Test Data)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
